import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { UserProfile, webpages_Role } from 'app/shared/models/domain/data.model';
import { UserClientLocation } from 'app/shared/models/response/user-client-location.model';

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;

    private _isoTokenName = 'isoAccessToken';
    private _isoUserName = 'isoUser';
    private _isoAuditorId = 'isoAuditorId';
    private _isoClientId = 'isoClientId';
    private _isoMmrId = 'isoMmrId';
    private _isoUserRolesName = 'isoUserRoles';
    private _scbUserClientLocations = 'scbUserClientLocations';

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService
    ) {
    }

    set localUserClientLocations(userClientLocations: UserClientLocation[]) {
        localStorage.setItem(this._scbUserClientLocations, JSON.stringify(userClientLocations));
    }

    get localUserClientLocations(): UserClientLocation[] {

        var raw = localStorage.getItem(this._scbUserClientLocations);

        if (raw) {
            return JSON.parse(raw) as UserClientLocation[];
        }

        return null;
    }

    set localUserRoles(roles: webpages_Role[]) {
        localStorage.setItem(this._isoUserRolesName, JSON.stringify(roles));
    }

    get localUserRoles(): webpages_Role[] {

        var raw = localStorage.getItem(this._isoUserRolesName);

        if (raw) {
            return JSON.parse(raw) as webpages_Role[];
        }

        return null;
    }

    set localUser(user: UserProfile) {
        localStorage.setItem(this._isoUserName, JSON.stringify(user));
    }

    get localUser(): UserProfile {

        var raw = localStorage.getItem(this._isoUserName);

        if (raw) {
            return JSON.parse(raw) as UserProfile;
        }

        return null;
    }

    set localAuditorId(auditorId: number) {
        localStorage.setItem(this._isoAuditorId, JSON.stringify({ auditorId: auditorId }));
    }

    get localAuditorId(): number {
        const raw = localStorage.getItem(this._isoAuditorId);
        if (raw) {
            return JSON.parse(raw)?.auditorId;
        }
        return null;
    }


    get localMmrId(): number {
        const raw = localStorage.getItem(this._isoMmrId);
        if (raw) {
            return JSON.parse(raw)?.mmrId;
        }
        return null;
    }

    set localMmrId(mmrId: number) {
        localStorage.setItem(this._isoMmrId, JSON.stringify({ mmrId: mmrId }));
    }

    set localClientId(clientId: number) {
        localStorage.setItem(this._isoClientId, JSON.stringify({ clientId: clientId }));
    }

    get localClientId(): number {
        const raw = localStorage.getItem(this._isoClientId);
        if (raw) {
            return JSON.parse(raw)?.clientId;
        }
        return null;
    }

    set accessToken(token: string) {
        localStorage.setItem(this._isoTokenName, token);
    }

    get accessToken(): string {
        return localStorage.getItem(this._isoTokenName) ?? '';
    }

    setLocalUserData(user: UserProfile): void {
        localStorage.setItem(this._isoUserName, JSON.stringify(user));
    }

    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post(environment.apiBaseUrl + 'api/auth/password/reset', { email: email });
    }

    resetPassword(payload: any): Observable<any> {
        return this._httpClient.post(environment.apiBaseUrl + 'api/auth/password', payload);
    }

    changePassword(password: string, userId: any): Observable<any> {
        if (userId !== null) {
            return this._httpClient.post(environment.apiBaseUrl + 'api/data/admin/users/change-password', {
                password: password,
                userId: userId
            });
        }
        return this._httpClient.post(environment.apiBaseUrl + 'api/data/admin/users/change-password', { password: password });
    }

    signIn(credentials: { email: string; password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post(environment.apiBaseUrl + 'api/auth/login', credentials).pipe(
            switchMap((response: any) => {
                // Store the access token in the local storage
                this.accessToken = response.accessToken;
                this.localUser = response.user;
                if (response?.auditorId) {
                    this.localAuditorId = response?.auditorId;
                }

                if (response?.clientId) {
                    this.localClientId = response?.clientId;
                }
                if (response?.mmrId) {
                    this.localMmrId = response?.mmrId;
                }
                this.localUserRoles = response.roles;
                this.localUserClientLocations = response.userClientLocations;

                // Set the authenticated flag to true
                this._authenticated = true;
                // Store the user on the user service
                this._userService.user = response.user;
                // Return a new observable with the response
                return of(response);
            })
        );
    }

    signInUsingToken(): Observable<any> {
        // Renew token
        return this._httpClient.post(environment.apiBaseUrl + 'api/auth/refresh-token', {
            accessToken: this.accessToken
        }).pipe(
            catchError(() =>
                // Return false
                of(false)
            ),
            switchMap((response: any) => {
                if (!response || !response.accessToken) {
                    // Return false
                    return of(false);
                }
                // Store the access token in the local storage
                this.accessToken = response.accessToken;
                // Set the authenticated flag to true
                this._authenticated = true;
                // Store the user on the user service
                this._userService.user = response.user;
                // Return true
                return of(true);
            })
        );
    }

    profileUsingToken(): Observable<any> {
        return this._httpClient.post(environment.apiBaseUrl + 'api/auth/user-details-by-token', {
            accessToken: this.accessToken
        }).pipe(
            catchError(() =>
                of(false)
            ),
            switchMap((response: any) => {
                this._userService.user = response.userProfile;
                return of(response);
            })
        );
    }

    saveProfileUsingToken(model): Observable<any> {
        model.accessToken = this.accessToken;
        return this._httpClient.post(environment.apiBaseUrl + 'api/auth/save-user-details-by-token', model).pipe(
            catchError(() =>
                of(false)
            ),
            switchMap((response: any) => {
                this._userService.user = response.userProfile;
                return of(response);
            })
        );
    }

    signOut(): Observable<any> {
        // Remove the access token from the local storage
        this.clearLocalStorage();

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    clearLocalStorage(): any {
        localStorage.removeItem(this._isoUserRolesName);
        localStorage.removeItem(this._isoUserName);
        localStorage.removeItem('isoAccessToken');
        localStorage.removeItem('isoAuditorId');
        localStorage.removeItem('isoClientId');
        localStorage.removeItem('isoMmrId');
        localStorage.removeItem('scbFilterUserListKey');
        localStorage.removeItem('scbUserClientLocations');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('isoDashboardTabIndex');
    }

    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    register(user: any): Observable<any> {
        return this._httpClient.post(environment.apiBaseUrl + 'api/auth/register', user).pipe(
            switchMap((response: any) => {
                // Store the access token in the local storage
                this.accessToken = response.accessToken;
                this.localUser = response.user;
                if (response?.auditorId) {
                    this.localAuditorId = response.auditorId;
                }

                if (response?.clientId) {
                    this.localClientId = response.clientId;
                }
                this.localUserRoles = response.roles;
                this.localUserClientLocations = response.userClientLocations;

                // Set the authenticated flag to true
                this._authenticated = true;
                // Store the user on the user service
                this._userService.user = response.user;
                // Return a new observable with the response
                return of(response);
            })
        );;
    }

    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }
        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }
        // Check the access token expire date is not expired
        if (AuthUtils.isTokenExpired(this.accessToken, 1200)) {
            return of(false);
        }
        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // Check the access token expire date is not expired
        if (!AuthUtils.isTokenExpired(this.accessToken, 1200)) {
            this._authenticated = true;
            return of(true);
        }
        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
